<template>
  <base-module
    :definition="handlebarDefinitions"
    :use-active="true"
    :connected="true"
    :error="
      hasHandlebarMotorError ||
      motorStatus.highTemperature ||
      motorStatus.hasHadHighTemperature
    "
    :inactive="hasOtherBlockingMotorError"
  >
    <template #title> {{ t('modules.handlebar.index') }} </template>

    <template
      #left
      v-if="!hasHandlebarMotorError"
    >
      <!-- Click events for fine grained control, hold events for smooth continues control-->
      <!-- Motor controls are increased by 2 to cover the time it takes to recieve a new position and thus create a smooth moving motor -->
      <motor-controls
        @click-up="
          updatePosition(
            handlebarCurrentSetPointY,
            motorPosition.handlebarY + 1,
          )
        "
        @click-down="
          updatePosition(
            handlebarCurrentSetPointY,
            motorPosition.handlebarY - 1,
          )
        "
        @click-left="
          updatePosition(
            handlebarCurrentSetPointX,
            motorPosition.handlebarX + 1,
          )
        "
        @click-right="
          updatePosition(
            handlebarCurrentSetPointX,
            motorPosition.handlebarX - 1,
          )
        "
        @hold-up="
          updatePosition(
            handlebarCurrentSetPointY,
            motorPosition.handlebarY + 5,
          )
        "
        @hold-down="
          updatePosition(
            handlebarCurrentSetPointY,
            motorPosition.handlebarY - 5,
          )
        "
        @hold-left="
          updatePosition(
            handlebarCurrentSetPointX,
            motorPosition.handlebarX + 5,
          )
        "
        @hold-right="
          updatePosition(
            handlebarCurrentSetPointX,
            motorPosition.handlebarX - 5,
          )
        "
      />
    </template>

    <template
      #right
      v-if="!hasHandlebarMotorError"
    >
      <n-form
        ref="formRef"
        label-placement="left"
      >
        <n-form-item label="X">
          <n-input-group v-if="isCalibrated && isLatestMeasurementCalibrated">
            <n-input-number
              :value="currentPositionX"
              :show-button="false"
              @keyup.enter="currentPositionX = $event.target.value"
            />
            <n-input
              v-if="positionChange"
              :value="String(positionChange.handlebarX * -1)"
              :show-button="false"
              disabled
              class="change-input"
            />
            <n-input-group-label>{{
              handlebarCurrentPositionX.unit
            }}</n-input-group-label>
          </n-input-group>
          <n-input
            v-else
            :value="t('common.notCalibrated')"
            :show-button="false"
            disabled
          ></n-input>
        </n-form-item>

        <n-form-item label="Y">
          <n-input-group v-if="isCalibrated && isLatestMeasurementCalibrated">
            <n-input-number
              :value="currentPositionY"
              :show-button="false"
              @keyup.enter="currentPositionY = $event.target.value"
            />
            <n-input
              v-if="positionChange"
              :value="positionChange.handlebarY"
              :show-button="false"
              disabled
              class="change-input"
            />
            <n-input-group-label>
              {{ handlebarCurrentPositionY.unit }}
            </n-input-group-label>
          </n-input-group>
          <n-input
            v-else
            :value="t('common.notCalibrated')"
            :show-button="false"
            disabled
          ></n-input>
        </n-form-item>
      </n-form>
    </template>

    <template
      #middle
      v-if="hasHandlebarMotorError"
    >
      <MotorError :device="deviceType.handlebar" />
    </template>
  </base-module>
</template>

<script setup lang="ts">
import {
  NInputNumber,
  NInput,
  NInputGroup,
  NInputGroupLabel,
  NForm,
  NFormItem,
  useMessage,
} from 'naive-ui'
import BaseModule from './BaseModule.vue'
import MotorControls from '@/components/controls/common/MotorControls.vue'
import MotorError from '@/components/controls/common/MotorError.vue'

import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { usePositionStore } from '@/store/positionStore'
import { useDeviceStore } from '@/store/deviceStore'
import { useI18n } from 'vue-i18n'
import { deviceType } from '@/types/device'
import { MqttValue } from '@/mqttDefinitions'

const { t } = useI18n()
const positionStore = usePositionStore()
const deviceStore = useDeviceStore()
const message = useMessage()

import {
  handlebarDefinitions,
  handlebarCurrentPositionX,
  handlebarCurrentPositionY,
  handlebarCurrentSetPointX,
  handlebarCurrentSetPointY,
} from '@/mqttDefinitions'

const { submitPosition } = positionStore
const { absolutePosition, positionOffset, motorPosition, positionChange } =
  storeToRefs(positionStore)
const { isCalibrated, isLatestMeasurementCalibrated } =
  storeToRefs(positionStore)
const { motorStatus } = storeToRefs(deviceStore)

const updatePosition = (definition: MqttValue, newValue: number) => {
  if (!submitPosition(definition, newValue)) {
    message.error(t('setup.calibrate.outOfReach'))
  }
}

const currentPositionX = computed({
  get() {
    return isCalibrated.value
      ? Math.abs(absolutePosition.value.handlebarX)
      : absolutePosition.value.handlebarX * -1
  },
  set(newValue) {
    const isNegative = absolutePosition.value.handlebarX < 0
    const valueToSet = isNegative ? -Math.abs(newValue) : Math.abs(newValue)

    updatePosition(
      handlebarCurrentSetPointX,
      valueToSet - positionOffset.value.handlebarX,
    )
  },
})

const currentPositionY = computed({
  get() {
    return absolutePosition.value.handlebarY
  },
  set(newValue) {
    updatePosition(
      handlebarCurrentSetPointY,
      newValue - positionOffset.value.handlebarY,
    )
  },
})

const hasHandlebarMotorError = computed(() => {
  return (
    motorStatus.value.handleBar.x.overcurrent ||
    motorStatus.value.handleBar.y.overcurrent ||
    motorStatus.value.handleBar.x.stalled ||
    motorStatus.value.handleBar.y.stalled ||
    motorStatus.value.handleBar.x.limitSwitch ||
    motorStatus.value.handleBar.y.limitSwitch ||
    motorStatus.value.highTemperature ||
    motorStatus.value.hasHadHighTemperature
  )
})

const hasOtherBlockingMotorError = computed(() => {
  return (
    motorStatus.value.saddle.x.overcurrent ||
    motorStatus.value.saddle.y.overcurrent ||
    motorStatus.value.saddle.x.stalled ||
    motorStatus.value.saddle.y.stalled ||
    motorStatus.value.saddle.x.limitSwitch ||
    motorStatus.value.saddle.y.limitSwitch
  )
})
</script>

<style lang="scss">
@media only screen and (max-width: 500px) {
  .n-input.change-input {
    display: none;
  }
}
</style>
